"use client";

import React, { useState, useEffect } from "react";
import { Rocket, MapPin, Phone, Mail, Globe, Crosshair } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactUs() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222321" id="lets-build-together" className="bg-background text-foreground relative overflow-hidden ![padding:30px_0px_96px_0px]">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-30">
        <div className="bg-primary/10 absolute h-[500px] w-[500px] rounded-md blur-[120px]" />
        <div className="bg-primary/5 absolute right-0 top-0 h-[300px] w-[300px] translate-x-1/2 -translate-y-1/2 rounded-md blur-[100px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          
          {}
          <div ref={leftRef} className={cn("flex flex-col gap-8 transition-all duration-1000 ease-out", leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12")}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-mono font-medium mb-6">
                <Crosshair className="size-4" />
                <span>SYSTEM_INIT</span>
              </div>
              <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground tracking-tight mb-4">
                Initiate <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">Development</span>
              </h2>
              <p className="font-default text-muted-foreground text-lg max-w-md">Ready to scale your digital infrastructure? Our engineering team is prepared to translate your requirements into a high-performance solution.</p>
            </div>

            {}
            <div className="flex flex-col gap-6">
              
            </div>

            {}
            
          </div>

          {}
          <div ref={rightRef} className={cn("relative transition-all duration-1000 ease-out delay-200", rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12")}>
            <div className="absolute -inset-1 bg-gradient-to-b from-primary/10 to-transparent rounded-md blur-xl opacity-30"></div>
            <div className="relative bg-card/80 backdrop-blur-2xl border border-border rounded-md p-8 md:p-10 shadow-2xl">
              <div className="mb-8">
                <h3 className="font-default text-2xl font-bold text-foreground mb-2">Project Brief</h3>
                <p className="font-default text-muted-foreground text-sm">Transmit your technical specifications to our secure intake server.</p>
              </div>

              <FormProvider formKey="build_together_form_start_a_project" formId="build_together_form_start_a_project" sectionName="contact-us" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true
              }
            }} submitText="Deploy Message" successMessage="Transmission received. Our lead architect will review your brief shortly." errorMessage="Transmission error. Please verify your network connection and retry.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [launching, setLaunching] = useState(false);
  useEffect(() => {
    if (isSubmitting) {
      setLaunching(true);
    } else if (!isSubmitting && isSubmitted) {
      setTimeout(() => setLaunching(false), 1000);
    } else {
      setLaunching(false);
    }
  }, [isSubmitting, isSubmitted]);
  if (isSubmitted && !submitError && !launching) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-md bg-primary/10 flex items-center justify-center mb-6">
          <Rocket className="size-8 text-primary" />
        </div>
        <h4 className="font-default text-xl font-semibold text-foreground mb-2">Handshake Complete</h4>
        <p className="font-default text-muted-foreground mb-8 max-w-xs">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md px-8 font-mono text-xs uppercase tracking-widest">
          New Transmission
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="relative pt-6">
              <FormControl>
                <Input {...field} className="peer bg-transparent border-b border-x-0 border-t-0 rounded-md border-border focus-visible:border-primary focus-visible:ring-0 px-0 h-10 placeholder-transparent text-foreground transition-colors font-default" placeholder="John Doe" />
              </FormControl>
              <FormLabel className="absolute left-0 top-2 text-xs font-mono uppercase tracking-wider text-muted-foreground transition-all peer-placeholder-shown:top-8 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary pointer-events-none">
                Lead Name
              </FormLabel>
              <FormMessage className="absolute -bottom-6 text-xs font-mono" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem className="relative pt-6">
              <FormControl>
                <Input type="email" {...field} className="peer bg-transparent border-b border-x-0 border-t-0 rounded-md border-border focus-visible:border-primary focus-visible:ring-0 px-0 h-10 placeholder-transparent text-foreground transition-colors font-default" placeholder="dev@agency.com" />
              </FormControl>
              <FormLabel className="absolute left-0 top-2 text-xs font-mono uppercase tracking-wider text-muted-foreground transition-all peer-placeholder-shown:top-8 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary pointer-events-none">
                Email Address
              </FormLabel>
              <FormMessage className="absolute -bottom-6 text-xs font-mono" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="relative pt-6">
            <FormControl>
              <Input type="tel" {...field} className="peer bg-transparent border-b border-x-0 border-t-0 rounded-md border-border focus-visible:border-primary focus-visible:ring-0 px-0 h-10 placeholder-transparent text-foreground transition-colors font-default" placeholder="+1 234 567 890" />
            </FormControl>
            <FormLabel className="absolute left-0 top-2 text-xs font-mono uppercase tracking-wider text-muted-foreground transition-all peer-placeholder-shown:top-8 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary pointer-events-none">
              Contact Number
            </FormLabel>
            <FormMessage className="absolute -bottom-6 text-xs font-mono" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Project details are required"
    }} render={({
      field
    }) => <FormItem className="relative pt-6">
            <FormControl>
              <Textarea {...field} className="peer bg-transparent border-b border-x-0 border-t-0 rounded-md border-border focus-visible:border-primary focus-visible:ring-0 px-0 min-h-[100px] resize-none placeholder-transparent text-foreground transition-colors font-default" placeholder="Describe your technical requirements..." />
            </FormControl>
            <FormLabel className="absolute left-0 top-2 text-xs font-mono uppercase tracking-wider text-muted-foreground transition-all peer-placeholder-shown:top-8 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary pointer-events-none">
              Project Scope
            </FormLabel>
            <FormMessage className="absolute -bottom-6 text-xs font-mono" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-xs font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting || launching} className="w-full h-14 rounded-md group relative overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-primary/20 transition-all duration-300 font-mono uppercase tracking-widest">
        <span className={cn("absolute inset-0 flex items-center justify-center gap-2 transition-transform duration-700 ease-in-out", launching ? "-translate-y-full opacity-0" : "translate-y-0 opacity-100")}>
          Deploy Message
          <Rocket className="size-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
        </span>
        
        <span className={cn("absolute inset-0 flex items-center justify-center gap-2 transition-transform duration-700 ease-in-out", launching ? "translate-y-0 opacity-100" : "translate-y-full opacity-0")}>
          Compiling...
          <Rocket className="size-5 animate-bounce" />
        </span>
      </Button>
    </Form>;
}